import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { MyApplList } from './MyApplList';
import { VseAlertContainer } from '../alert/VseAlertContainer';
import * as SharedData from '../../features/sharedData';
import * as MyAppl from '../../features/myAppl';
import * as VseAlert from '../../features/alert';
import { VseApplicationEntity, DotNetValidationProblemDetailsTyped, VseValidation } from 'vse-material-ui';

const useStyles = makeStyles((theme: Theme) => ({
  main: {
    flex: 1,
    padding: theme.spacing(1, 1),
  },
  paper: {
    margin: 'auto',
    overflow: 'hidden',
  },
  appBar: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  applyButton: {
    marginRight: theme.spacing(1),
  },
  alert: {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
  },
  toolBarContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    //paddingLeft: theme.spacing(2),
  },
  sectionTitle: {
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  sectionRow: {
    paddingLeft: theme.spacing(2),
  },
  hidden: {
    display: 'none',
  },
}));

export function MyApplPage() {
  function getApplStatus(applStatus: string) {
    if (sharedDataState.applStatusLoading === 'succeeded') {
      return sharedDataState.applStatusOptions.find(e => e.code === applStatus);
    }
    return undefined;
  }

  function getClass(classID: number) {
    if (myApplState.loading === 'succeeded') {
      return myApplState.classes.find(e => e.classID === classID);
    }
    return undefined;
  }

  function getCourse(universityID: number, seasonID: number, termCode: string, courseCode: string) {
    if (myApplState.loading === 'succeeded') {
      return myApplState.courses.find(e => {
        return e.universityID === universityID &&
          e.seasonID === seasonID &&
          e.termCode === termCode &&
          e.courseCode === courseCode;
      });
    }
    return undefined;
  }

  function getSeason(seasonID: number) {
    if (myApplState.loading === 'succeeded') {
      return myApplState.seasons.find(e => e.seasonID === seasonID);
    }
    return undefined;
  }

  function getUniversity(universityID: number) {
    if (sharedDataState.universityLoading === 'succeeded') {
      return sharedDataState.universityOptions.find(e => e.universityID === universityID);
    }
    return undefined;
  }

  function getUniversityTerm(universityID: number, seasonID: number, termCode: string) {
    if (myApplState.loading === 'succeeded') {
      return myApplState.universityTerms.find(e => {
        return e.universityID === universityID &&
          e.seasonID === seasonID &&
          e.termCode === termCode;
      });
    }
    return undefined;
  }

  function handleApplyClick() {
    let valProblems: DotNetValidationProblemDetailsTyped<VseApplicationEntity> = VseValidation.createValidationProblemDetails();
    if (seasonID === '' || seasonID === undefined) {
      VseValidation.appendErrorTo(valProblems, "seasonSelect", "Please select a season for application");
      dispatch(VseAlert.alertValidationProblem(valProblems));
    } else {
      history.push(`/apply/${seasonID}`);
    }
  };

  const theme = useTheme();
  const classes = useStyles(theme);
  let history = useHistory();
  const dispatch = useDispatch();
  const sharedDataState = SharedData.useVseSharedDataState();
  const myApplState = MyAppl.useMyApplState();
  //const seasonID = myApplState.seasonsOpening.length > 0 ? myApplState.seasonsOpening[0].seasonID : undefined;
  const [seasonID, setSeasonID] = React.useState<undefined | string>('');

  React.useEffect(() => {
    dispatch(MyAppl.load());
    return () => {
      dispatch(VseAlert.reset());
      dispatch(MyAppl.reset());
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (sharedDataState.applStatusLoading === 'idle') {
      dispatch(SharedData.loadApplStatuses());
    }
  }, [dispatch, sharedDataState.applStatusLoading]);

  React.useEffect(() => {
    if (sharedDataState.universityLoading === 'idle') {
      dispatch(SharedData.loadUniversities());
    }
  }, [dispatch, sharedDataState.universityLoading]);

  return (
    <main className={classes.main}>
      <React.Fragment>
        <VseAlertContainer className={classes.alert} />
        <Paper className={classes.paper}>
          {(myApplState.loading !== 'succeeded' && myApplState.loading !== 'failed') ? (
            <Backdrop open={true}>
              <CircularProgress color="inherit" />
            </Backdrop>
          ) : (
            <React.Fragment>
              <AppBar className={classes.appBar} position="static" color="default" elevation={0}>
                <Toolbar>
                  <Grid container className={classes.toolBarContainer} >
                    <Grid item xs={12} md={4} className={clsx(myApplState.seasonsOpening && myApplState.seasonsOpening.length > 0 && classes.hidden)} >
                      <Typography >No VSE Season is currently open for application.</Typography>
                    </Grid>
                    <Grid item xs={12} md={4} className={clsx(myApplState.seasonsOpening && myApplState.seasonsOpening.length === 0 && classes.hidden)}>
                      <TextField id='Season' label='Select VSE Season to apply' InputProps={{
                        readOnly: false,
                      }}
                        select required
                        value={seasonID}
                        onChange={(ev) => {
                          setSeasonID(ev.target.value);
                          //console.log(ev.target.value, 'select');
                        }}
                      >
                        {myApplState.seasonsOpening.map((option) => (
                          <MenuItem key={option.seasonID} value={option.seasonID}>
                            {option.shortDescription}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button
                        id="apply-button"
                        variant="contained"
                        color="primary"
                        disabled={myApplState.seasonsOpening.length === 0}
                        className={classes.applyButton}
                        onClick={() => { handleApplyClick(); }}
                      >
                        Apply
                      </Button>
                    </Grid>
                  </Grid>
                </Toolbar>
              </AppBar>
              <div className={classes.sectionTitle}>
                <Grid item xs={12} md={12} component={Typography} variant='h6'>My Applications</Grid>
              </div>
              {myApplState.myAppl.length > 0 ? (
                <MyApplList
                  data={myApplState.myAppl}
                  getClass={getClass}
                  getCourse={getCourse}
                  getSeason={getSeason}
                  getUniversity={getUniversity}
                  getUniversityTerm={getUniversityTerm}
                  getApplStatus={getApplStatus}
                />
              ) : (
                <div className={classes.sectionRow}>
                  <Typography>No application found.</Typography>
                </div>
              )
              }
            </React.Fragment>
          )
          }
        </Paper>
      </React.Fragment>
    </main>
  );
}
